import { FC, useMemo } from 'react';
import DataTable from './DataTable';
import defaultProfileImage from '../../assets/images/default-profile.png';
import { EmployeeEntity } from '../../api/generated';
import { useTranslation } from 'react-i18next';
import { Box, Chip, IconButton, Tooltip } from '@mui/material';
import { AddCircleOutline } from '@mui/icons-material';
import CustomAppAvatar from 'components/avatars/CustomAppAvatar';
import { useSeason } from 'contexts/SeasonContext';
import '../../assets/css/underLineAnimation.css';

interface DataTableProps {
  data?: any[];
  handleRowSelect: (rows: []) => void;
  onAssignSeason: (employeeId: number) => void;
  onEmployeeClick: (employee: EmployeeEntity) => void;
}

const AllEmployeesDataTable: FC<DataTableProps> = (props) => {
  const { t } = useTranslation();
  const { seasonId } = useSeason();

  const getColumnShape = () => {
    return [
      {
        Header: t('employees.generalInformation.name'),
        accessor: (row: any) =>
          `${row.firstName?.toUpperCase()} ${row.lastName?.toUpperCase()}`,
        Cell: ({ row }: any) => {
          const employee = row.original as EmployeeEntity;
          const displayAvatar = employee.filePath || defaultProfileImage;
          return (
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
              }}
              onClick={() => props.onEmployeeClick(employee)}
              className='hover-underline-animation-parent'
            >
              <CustomAppAvatar src={displayAvatar} />
              <Box className='box' style={{ marginLeft: '10px' }}>
                <span className='hover-underline-animation'>
                  {`${employee?.firstName} ${employee?.lastName}`}
                </span>
              </Box>
            </Box>
          );
        },
      },
      {
        Header: t('employees.generalInformation.email'),
        accessor: 'email',
      },
      {
        Header: t('employees.generalInformation.role'),
        accessor: 'role',
        Cell: ({ value }: any) => <Chip label={value} size='small' />,
      },
      {
        Header: t('menu.management.seasons'),
        accessor: (row: any) =>
          row.employeesToSeasons
            ?.map((es: any) => es.season?.name)
            .join(', ') || '-',
        Cell: ({ row }: any) => {
          const employee = row.original as EmployeeEntity;
          const seasons = employee.employeesToSeasons || [];
          if (seasons.length === 0) return <span>-</span>;
          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {seasons.map((es) => (
                <Chip
                  key={es.seasonId}
                  label={es.season?.name || ''}
                  size='small'
                  color={es.seasonId === seasonId ? 'primary' : 'default'}
                  variant={es.seasonId === seasonId ? 'filled' : 'outlined'}
                />
              ))}
            </Box>
          );
        },
      },
      {
        Header: t('common.actions'),
        Cell: ({ row }: any) => {
          const employee = row.original as EmployeeEntity;
          const isAssignedToCurrentSeason = employee.employeesToSeasons?.some(
            (es) => es.seasonId === seasonId,
          );

          if (isAssignedToCurrentSeason) {
            return (
              <Chip
                label={t('employees.status.assigned')}
                color='success'
                size='small'
                variant='outlined'
              />
            );
          }

          return (
            <Tooltip title={t('employees.actions.addToSeason')}>
              <IconButton
                onClick={() => props.onAssignSeason(employee.id)}
                color='primary'
              >
                <AddCircleOutline />
              </IconButton>
            </Tooltip>
          );
        },
      },
    ];
  };

  const columns: any = useMemo(() => getColumnShape(), [t, seasonId]);

  return (
    <DataTable
      columns={columns}
      data={props.data || []}
      handleRowSelect={props.handleRowSelect}
    />
  );
};

export default AllEmployeesDataTable;
